import { axios, AxiosResponse } from '@/config/plugins/axios';
import { Filter, Pagination, Payment } from '@/types';
import { downloadUTF16CsvFile } from '@/functions/downloadFile';
import dayjs from 'dayjs';
import notificationManager from '@/services/Notification/NotificationManager';
import { paymentTransition } from '@/services/api/payment/paymentTransitionBuilder';

/**
 * Gère les paiements.
 */
const paymentService = {
	/**
	 * Récupère les paiments filtrés en fonction du statut
	 */
	fetchAllPayments(
		pagination: Pagination,
		search?: string,
		projectToken?: string | null,
		type?: string | null,
		scope?: string | null,
		advancedSearch?: any
	): Promise<AxiosResponse<any>> {
		scope = scope ? scope : null;
		const params = {
			scope,
			filters: [] as Filter[],
			itemsPerPage: pagination.itemsPerPage,
			page: pagination.page,
			sortDesc: pagination.sortDesc,
			sortBy: pagination.sortBy,
			search,
			advancedSearch
		};

		if (0 === pagination.sortBy?.length) {
			params.sortBy = null;
			params.sortDesc = null;
		}

		const uri = (projectToken) ? `/api/projects/${projectToken}/payments` : `/api/payments/type/${type}`;

		return axios.get(uri, { params });
	},

	/**
	 * Mets à jour le statut de plusieurs paiements.
	 */
	postPaymentsTransition(paymentTransition: paymentTransition[]): Promise<AxiosResponse<any>> {
		const payments = { paymentsTransition: paymentTransition };

		return axios.post('/api/payments/transition', payments);
	},

	/**
	 * Exporte des paiements par Ids.
	 * Note : les payments id sont convertis en string pour fonctionner avec l'interface Filter
	 */
	exportPayments(scope: string, payments: Payment[]): void {
		const paymentsIds = [] as string[];
		const params = {
			filters: [] as Filter[],
			scope
		};

		payments.forEach((payment: Payment) => {
			paymentsIds.push(JSON.stringify(payment.id));
		});

		params.filters.push({
			field: 'id',
			operator: 'in',
			values: paymentsIds
		});

		const today = dayjs().format('DD-MM-YYYY');
		const fileName = `Liste des paiements (${today})`;

		axios
			.post('/api/exports/payments', params)
			.then((response) => {
				downloadUTF16CsvFile(response.data, `${fileName}.csv`);
			});
	},

	/**
	 *  Modifie le statut de plusieurs paiements de façon asynchrone
	 */
	updateAllPayments(paymentTransition: paymentTransition[]): void {
		const payments = { paymentsTransition: paymentTransition };
		axios.post('/api/payments/transition/async', payments)
			.then((response: AxiosResponse) => {
				notificationManager.displayMessage('success', response.data);
			})
			.catch((error) => {
				notificationManager.displayMessage('error', error.response.data.message);
			});
	},

	/**
	 * Approuve tous les paiements au statut 'à approuver' en asynchrone selon le type choisi.
	 */
	approveAllPaymentsByType(type: string): void {
		axios.post(`/api/payments/type/${type}/approve/async`)
			.then((response: AxiosResponse) => {
				notificationManager.displayMessage('success', response.data);
			})
			.catch((error) => {
				notificationManager.displayMessage('error', error.response.data.message);
			});
	},

	/**
	 * Exporte tous les paiements (par scope & filtres).
	 */
	exportPaymentsByFilters(scope: string, filters: string[] | null): void {
		const params = {
			filters: [] as Filter[],
			scope
		};

		if (filters) {
			params.filters.push({
				field: 'status',
				operator: 'in',
				values: filters
			});
		}

		const today = dayjs().format('DD-MM-YYYY');
		const fileName = `Liste des paiements (${today})`;

		axios
			.post('/api/exports/payments', params)
			.then((response) => {
				downloadUTF16CsvFile(response.data, `${fileName}.csv`);
			});
	},

	/**
	 * Exporte tous les paiements (par projet & filtres).
	 */
	exportPaymentsByProject(projectToken: string, filters: string[] | null): void {
		const params = {
			filters: [] as Filter[]
		};

		if (filters) {
			params.filters.push({
				field: 'status',
				operator: 'in',
				values: filters
			});
		}

		const today = dayjs().format('DD-MM-YYYY');
		const fileName = `Liste des paiements (${today})`;

		axios
			.get(`/api/exports/projects/${projectToken}/payments`, { params })
			.then((response) => {
				downloadUTF16CsvFile(response.data, `${fileName}.csv`);
			});
	},

	/**
	 * Suppression d'un paiement Non approuvé
	 */
	deletePayment(paymentId: number): Promise<AxiosResponse<any>> {
		return axios.delete(`/api/payments/${paymentId}`);
	},

	fetchNonSupportPaymentsCount(scope: string, type: string) {
		return axios.get(`/api/payments/type/${type}/scope/${scope}/count`);
	},

	getPaymentsAdvancedSearch(projectToken?: string | null, scope?: string | null, type?: string | null) {
		const uri = (projectToken) ? `/api/projects/${projectToken}/payments/advanced-search` : `/api/payments/type/${type}/advanced-search`;

		const params = {
			scope
		};

		return axios.get(uri, { params });
	},

	getPaymentRejectionReasons() {
		return axios.get('/api/payment-rejection-reasons');
	},

	/**
	 * Vérification de dépassement budgétaire des paiements avant approbation.
	 */
	preApprovePaymentsSync(paymentTransition: paymentTransition[], type: string): Promise<AxiosResponse<any>> {
		const payments = { paymentsTransition: paymentTransition };

		return axios.post(`/api/payments/type/${type}/approve/validation/sync`, payments);
	},

	/**
	 * Vérification de dépassement budgétaire des paiements avant approbation.
	 */
	preApprovePaymentsAsync(): Promise<AxiosResponse<any>> {
		return axios.post('/api/payments/approve/validation/async');
	},

	/**
	 * Suppression d'une reprise sur flux sur un paiement
	 */
	deletePaymentDebit(paymentIdList: number[]): Promise<AxiosResponse<any>> {
		const payments = { payments: paymentIdList };

		return axios.post('/api/payments/payment-debit/delete', payments);
	}
};

export default paymentService;
